import type {
  BookingCoordinates,
  BookingLocation,
  LocationGeocodedName,
} from '@/entities/bookings/booking.entity';
import type { LocationDto } from '@/entities/station/station.entity';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import { pointToLocationDto } from '@/hooks/use-location-to-coordinates';

/** Keep only the fields that belong to the selected location type. */
export const exclusiveBookingLocation = (location: BookingLocation): BookingLocation =>
  location.locationType === BookingLocationType.STATION
    ? { ...location, location: null }
    : { ...location, station: null };

type CreateLocationFields<Prefix extends 'pickup' | 'dropoff'> = {
  [K in `${Prefix}LocationType`]: BookingLocationType;
} & {
  [K in `${Prefix}StationId`]?: string;
} & {
  [K in `${Prefix}Location`]?: BookingCoordinates | null;
} & {
  [K in `${Prefix}LocationGeocodedAddress`]?: LocationGeocodedName | null;
};

export function bookingLocationToCreateFields<Prefix extends 'pickup' | 'dropoff'>(
  prefix: Prefix,
  location: BookingLocation,
): CreateLocationFields<Prefix> {
  const exclusive = exclusiveBookingLocation(location);
  return {
    [`${prefix}LocationType`]: exclusive.locationType,
    [`${prefix}StationId`]: exclusive.station?.id,
    [`${prefix}Location`]: exclusive.location?.coordinates,
    [`${prefix}LocationGeocodedAddress`]: exclusive.location?.address,
  } as CreateLocationFields<Prefix>;
}

export const bookingLocationToLocationDto = (
  location: BookingLocation | null,
): LocationDto | undefined | null => {
  if (!location) {
    return undefined;
  }
  if (location.locationType === BookingLocationType.STATION) {
    return pointToLocationDto(location.station?.location);
  }
  return location.location?.coordinates;
};
